import {
  Card,
  CardBody,
  CardHeader,
  Heading,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Box,
  Text,
  Skeleton,
} from '@chakra-ui/react'
import {
  LineChart as RechartsLineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts'

interface ActivityTrendsTabsProps {
  moduleActivities: Array<{
    date: string
    label: string
    total: number
    by_module: Record<string, number>
  }>
  perModuleData: Array<{
    module: string
    total: number
    daily_trends: Array<{
      date: string
      label: string
      count: number
    }>
  }>
  userRegistrationTrends: Array<{
    date: string
    label: string
    count: number
  }>
  activityTrends: Array<{
    date: string
    label: string
    count: number
  }>
  hourlyTrends: {
    today: number[]
    yesterday: number[]
  }
  isLoading?: boolean
}

const MODULE_COLORS = ['#3182CE', '#38A169', '#DD6B20', '#805AD5', '#D69E2E', '#E53E3E', '#9F7AEA', '#ED64A6', '#F56565', '#48BB78']

export function ActivityTrendsTabs({
  moduleActivities,
  perModuleData,
  userRegistrationTrends,
  activityTrends,
  hourlyTrends,
  isLoading = false,
}: ActivityTrendsTabsProps) {
  // Prepare module activities chart data
  const monthlyModuleActivitiesData =
    perModuleData.length > 0
      ? (() => {
          const firstModule = perModuleData[0]
          if (!firstModule?.daily_trends) return []

          return firstModule.daily_trends.map((day) => {
            const dataPoint: Record<string, any> = {
              date: day.date,
              label: day.label,
            }

            perModuleData.forEach((moduleData) => {
              const dayData = moduleData.daily_trends.find((d) => d.date === day.date)
              dataPoint[moduleData.module] = dayData?.count ?? 0
            })

            const total = Object.values(dataPoint)
              .filter((v) => typeof v === 'number')
              .reduce((sum: number, val: any) => sum + val, 0)
            dataPoint.Total = total

            return dataPoint
          })
        })()
      : []

  // Prepare hourly chart data
  const hourlyChartData =
    hourlyTrends && hourlyTrends.today.length === 24
      ? Array.from({ length: 24 }, (_, i) => ({
          hour: i,
          name: `${i}:00`,
          Today: hourlyTrends.today[i] ?? 0,
          Yesterday: hourlyTrends.yesterday[i] ?? 0,
        }))
      : []

  return (
    <Card bg="white" borderRadius="md" boxShadow="sm" flexShrink={0} w="100%">
      <CardHeader>
        <Heading size="md">Activity Trends</Heading>
        <Text fontSize="sm" color="gray.500">
          View different activity metrics and trends
        </Text>
      </CardHeader>
      <CardBody pt={0}>
        <Tabs colorScheme="blue">
          <TabList>
            <Tab>Module Activities</Tab>
            <Tab>User Registration</Tab>
            <Tab>Overall Activity</Tab>
            <Tab>Hourly Activity</Tab>
          </TabList>

          <TabPanels>
            {/* Module Activities Tab */}
            <TabPanel px={0}>
              {isLoading ? (
                <Box h="400px" w="100%" display="flex" alignItems="center" justifyContent="center">
                  <Skeleton height="350px" width="100%" />
                </Box>
              ) : monthlyModuleActivitiesData.length > 0 && perModuleData.length > 0 ? (
                <Box h="450px" w="100%">
                  <ResponsiveContainer width="100%" height="100%">
                    <RechartsLineChart
                      data={monthlyModuleActivitiesData}
                      margin={{ top: 5, right: 30, left: 0, bottom: 20 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
                      <XAxis
                        dataKey="label"
                        tick={{ fontSize: 10 }}
                        angle={-45}
                        textAnchor="end"
                        height={80}
                      />
                      <YAxis tick={{ fontSize: 11 }} />
                      <Tooltip contentStyle={{ fontSize: '12px' }} labelStyle={{ fontWeight: 'bold' }} />
                      <Legend wrapperStyle={{ fontSize: '11px', paddingTop: '10px' }} iconType="line" />
                      <Line
                        type="monotone"
                        dataKey="Total"
                        stroke="#1A202C"
                        strokeWidth={3}
                        dot={{ r: 2 }}
                        name="Total Activities"
                      />
                      {perModuleData.map((moduleData, index) => (
                        <Line
                          key={moduleData.module}
                          type="monotone"
                          dataKey={moduleData.module}
                          stroke={MODULE_COLORS[index % MODULE_COLORS.length]}
                          strokeWidth={2}
                          dot={{ r: 1 }}
                          name={`${moduleData.module} (${moduleData.total})`}
                        />
                      ))}
                    </RechartsLineChart>
                  </ResponsiveContainer>
                </Box>
              ) : (
                <Box h="400px" w="100%" display="flex" alignItems="center" justifyContent="center">
                  <Text color="gray.500">No module activities data available</Text>
                </Box>
              )}
            </TabPanel>

            {/* User Registration Tab */}
            <TabPanel px={0}>
              {isLoading ? (
                <Box h="400px" w="100%" display="flex" alignItems="center" justifyContent="center">
                  <Skeleton height="350px" width="100%" />
                </Box>
              ) : userRegistrationTrends.length > 0 ? (
                <Box h="400px" w="100%">
                  <ResponsiveContainer width="100%" height="100%">
                    <RechartsLineChart
                      data={userRegistrationTrends}
                      margin={{ top: 5, right: 30, left: 0, bottom: 20 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
                      <XAxis dataKey="label" tick={{ fontSize: 10 }} angle={-45} textAnchor="end" height={80} />
                      <YAxis tick={{ fontSize: 11 }} />
                      <Tooltip contentStyle={{ fontSize: '12px' }} labelStyle={{ fontWeight: 'bold' }} />
                      <Line
                        type="monotone"
                        dataKey="count"
                        stroke="#3182CE"
                        strokeWidth={2}
                        dot={{ r: 3 }}
                        name="New Users"
                      />
                    </RechartsLineChart>
                  </ResponsiveContainer>
                </Box>
              ) : (
                <Box h="400px" w="100%" display="flex" alignItems="center" justifyContent="center">
                  <Text color="gray.500">No user registration data available</Text>
                </Box>
              )}
            </TabPanel>

            {/* Overall Activity Tab */}
            <TabPanel px={0}>
              {isLoading ? (
                <Box h="400px" w="100%" display="flex" alignItems="center" justifyContent="center">
                  <Skeleton height="350px" width="100%" />
                </Box>
              ) : activityTrends.length > 0 ? (
                <Box h="400px" w="100%">
                  <ResponsiveContainer width="100%" height="100%">
                    <RechartsLineChart
                      data={activityTrends}
                      margin={{ top: 5, right: 30, left: 0, bottom: 20 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
                      <XAxis dataKey="label" tick={{ fontSize: 10 }} angle={-45} textAnchor="end" height={80} />
                      <YAxis tick={{ fontSize: 11 }} />
                      <Tooltip contentStyle={{ fontSize: '12px' }} labelStyle={{ fontWeight: 'bold' }} />
                      <Line
                        type="monotone"
                        dataKey="count"
                        stroke="#38A169"
                        strokeWidth={2}
                        dot={{ r: 3 }}
                        name="Total Activities"
                      />
                    </RechartsLineChart>
                  </ResponsiveContainer>
                </Box>
              ) : (
                <Box h="400px" w="100%" display="flex" alignItems="center" justifyContent="center">
                  <Text color="gray.500">No activity trends data available</Text>
                </Box>
              )}
            </TabPanel>

            {/* Hourly Activity Tab */}
            <TabPanel px={0}>
              {isLoading ? (
                <Box h="400px" w="100%" display="flex" alignItems="center" justifyContent="center">
                  <Skeleton height="350px" width="100%" />
                </Box>
              ) : hourlyChartData.length > 0 ? (
                <Box h="400px" w="100%">
                  <ResponsiveContainer width="100%" height="100%">
                    <RechartsLineChart
                      data={hourlyChartData}
                      margin={{ top: 5, right: 30, left: 0, bottom: 20 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
                      <XAxis dataKey="name" tick={{ fontSize: 10 }} />
                      <YAxis tick={{ fontSize: 11 }} />
                      <Tooltip contentStyle={{ fontSize: '12px' }} labelStyle={{ fontWeight: 'bold' }} />
                      <Legend wrapperStyle={{ fontSize: '11px', paddingTop: '10px' }} />
                      <Line
                        type="monotone"
                        dataKey="Today"
                        stroke="#3182CE"
                        strokeWidth={2}
                        dot={{ r: 2 }}
                        name="Today"
                      />
                      <Line
                        type="monotone"
                        dataKey="Yesterday"
                        stroke="#718096"
                        strokeWidth={2}
                        dot={{ r: 2 }}
                        name="Yesterday"
                      />
                    </RechartsLineChart>
                  </ResponsiveContainer>
                </Box>
              ) : (
                <Box h="400px" w="100%" display="flex" alignItems="center" justifyContent="center">
                  <Text color="gray.500">No hourly activity data available</Text>
                </Box>
              )}
            </TabPanel>
          </TabPanels>
        </Tabs>
      </CardBody>
    </Card>
  )
}
